import {createContext, useEffect, useState} from "react";
import dayjs from "dayjs";

export const DatePickerContext = createContext({
  date: new Date(),
  visible: {
    month: 0,
    year: 1970,
  },
  view: "date",
  viewType: "date",
  nextMonth: () => {
  },
  prevMonth: () => {
  },
  nextYear: () => {
  },
  prevYear: () => {
  },
  nextDecade: () => {
  },
  prevDecade: () => {
  },
  selectMonth: (m) => {
  },
  selectYear: (y) => {
  },
  selectDate: (d) => {
  },
  viewMonths: () => {
  },
  viewYears: () => {
  },
  isVisible: false,
  showCalendar: () => {
  },
  toggleCalendar: () => {
  },
  isSelectedDate: (d) => false,
  clearCalendar: () => {
  }
});

export function useDatePicker({value, viewType, onChange, ref, name, min, max, onBlur, format}) {
  const date = dayjs(value || new Date());

  const [monthYear, setMonthYear] = useState({
    month: date.month(),
    year: date.year(),
  });

  const [view, setView] = useState(viewType === "date" ? "date" : "month");
  const [isVisible, setVisible] = useState(false);

  const setValue = (data) => {
    onBlur?.({
      target: {name},
    });

    onChange?.({
      target: {name, value: data},
    });
  };

  const selectDate = (d) => {
    const value = new Date(monthYear.year, monthYear.month, d);
    setValue(value);
    setVisible(false);
  };

  const isSelectedDate = (d) => {
    if (!validateDate(value, format)) return false;

    return d === date.date() &&
      monthYear.month === date.month() &&
      monthYear.year === date.year();
  };

  const selectMonth = (m) => {
    setMonthYear((state) => ({...state, month: m}));

    if (viewType === 'month') {
      setValue(new Date(monthYear.year, m, 1));
      setVisible(false);
      return;
    }

    setView("date");
  };

  const selectYear = (y) => {
    setMonthYear((state) => ({...state, year: y}));
    setView("month");
  };

  useEffect(() => {
    if (isVisible && validateDate(value, format)) {
      setMonthYear({month: date.month(), year: date.year()});
    }
  }, [isVisible, value]);

  useEffect(() => {
    function mouseDownListener(e) {
      if (ref.current && !ref.current.contains(e.target)) {

        if (typeof value === 'string') {
          const data = dayjs(value);
          setValue(!validateDate(value, format) ? value : max && data.isAfter(dayjs(max)) ? dayjs(max) : data);
        }

        setVisible(false);
      }
    }

    document.addEventListener("mousedown", mouseDownListener);

    return () => {
      document.removeEventListener("mousedown", mouseDownListener);
    };
  });

  return {
    visible: monthYear,
    view,
    viewType,
    nextMonth: () =>
      setMonthYear((state) =>
        state.month >= 11
          ? {month: 0, year: state.year + 1}
          : {month: state.month + 1, year: state.year}
      ),
    prevMonth: () =>
      setMonthYear((state) =>
        state.month <= 0
          ? {month: 11, year: state.year - 1}
          : {month: state.month - 1, year: state.year}
      ),
    nextYear: () =>
      setMonthYear((state) => ({...state, year: state.year + 1})),
    prevYear: () =>
      setMonthYear((state) => ({...state, year: state.year - 1})),
    nextDecade: () =>
      setMonthYear((state) => ({...state, year: state.year + 12})),
    prevDecade: () =>
      setMonthYear((state) => ({...state, year: state.year - 12})),
    selectMonth,
    selectYear,
    selectDate,
    viewMonths: () => setView("month"),
    viewYears: () => setView("year"),
    isVisible,
    showCalendar: () => setVisible(true),
    toggleCalendar: () => setVisible((state) => !state),
    isSelectedDate,
    clearCalendar: () => {
      setValue('');
      setVisible(false);
    },
    min,
    max
  };
}

export const validateDate = (date, format) => {
  return dayjs(date, format).format(format) === (typeof date === 'string' ? date : dayjs(date).format(format));
}